export const SUPPORTED_FEATURE_5GC_VPC = 'XC-CNFVCP';
export const SUPPORTED_FEATURE_N_PLUS_K = 'npluskgr';
export const SUPPORTED_FEATURE_NOKAMFLCSIM = 'nokamflcsim';
export const SUPPORTED_FEATURE_XCCA = 'xcca';
export const SUPPORTED_FEATURE_MULTITENANT = 'multitenant';

export type SupportedFeaturesAccessFunction = {
  requiredTargets?: string[]
  collectionFunctions?: string[]
  features: string[]
  autowire: boolean
  title: string
  models: string[]
  modelOptions?: ModelOptions
  versions: string[]
  genccc: boolean
  services: AFServiceSpecs | string[]
  optionalTargets?: string[]
  x3deliveryOptions?: null | string,
  x3TargetAssociations?: SupportedFeaturesX3TargetAssociations
  contentDeliveryVia?: string
  xcipioAFType: string
  description: string
  supportedActions?: string[]
  httpVersions?: string[]
  httpDefaultVersion?: string
  afProtocolVersions?: string[]
  targetOption?: SupportedFeaturesTargetOption
  vendor?: string
  type?: string
}

export type SupportedFeaturesCollectionFunction = {
  services: string[]
  versions: string[]
  features: string[]
  description: string
  title: string
  vendor?: string
  type?: string
  supersedes?: string[]
}

export type AFServiceSpecs = {
  [serviceKey: string]: {
    requiredTargets?: string[]
    optionalTargets?: string[]
    collectionFunctions?: string[]
  }
}

export type ModelOption = {
  supportX2?: boolean
  supportX3?: boolean
}

export type ModelOptions = {
  [modelName: string]: ModelOption
}

export type SupportedFeaturesX3TargetAssociations = {
  targetInfoType: string
  targets: string[]
  bAFAssociation?: boolean
}

export type SupportedFeaturesTargetOption = {
  type: 'byCollectionFunctionTag' | 'byService'
  values: {
    [key: string]: {
      key: string
      value: string
    }
  }
}

export type SupportedFeaturesAccessFunctions = {
  [accessFunctionTag: string]: SupportedFeaturesAccessFunction
}

export type SupportedFeaturesCollectionFunctions = {
  [collectionFunctionTag: string]: SupportedFeaturesCollectionFunction
}

export type OntologyService = {
  title: string
  description: string
  deployments?: string[]
  /**
   * The e-warrant service types this service covers, as they appear in an ETSI
   * LI task's serviceTypes. This is what lets an inbound e-warrant service be
   * turned into ontology services without a hardcoded translation table — the
   * ontology owns the correspondence.
   */
  ewsServiceTypes?: string[]
}

export type Ontology = {
  services: {
    [serviceKey: string]: OntologyService
  },
  accessFunctions: SupportedFeaturesAccessFunctions
  collectionFunctions: SupportedFeaturesCollectionFunctions
  targets: SupportedFeaturesTargets
};

/**
 * An ontology service this delivery function has enabled, carrying its key so the
 * entry stands on its own once it is out of the services map.
 */
export type EnabledService = OntologyService & {
  key: string
};

export type SupportedFeaturesTargets = {
  [targetKey: string]: SupportedFeaturesTarget
}

export type SupportedFeaturesTarget = {
  description: string
  title: string
  type: string
  pattern?: string
  maxLength?: number
  minLength?: number
  example?: string
  enum?: {
    value: string
    title: string
  }[],
  items?: {
    [itemName: string]: SupportedFeaturesTargetItem
  },
  derivedTarget?: string,
  targetOptions?: TargetOption[]
  /**
   * The e-warrant target types this Xcipio target type covers, as they appear in an
   * ETSI LI task's target identifier format (`InternationalE164`, `IMSIIdentifier`).
   * This is what lets an inbound e-warrant target be turned into Xcipio targets
   * without a hardcoded translation table — the ontology owns the correspondence.
   */
  ewsTargetTypes?: string[]
}

export type TargetOption = {
  type: string,
  key: string,
  title: string,
  pattern?: string,
  minLength?: number,
  maxLength?: number
}

export type SupportedFeaturesTargetItem = {
  type: string
  title: string
  key?: string
  enum?: {
    value: string
    title: string
  }[],
  pattern?: string,
  example?: string,
  maxLength?: number,
  minLength?: number,
}

export type SupportedFeatures = {
  id: string
  description: string
  features: {
    customer: string
    version: number
    nodes: SupportedFeaturesNode[]
  }
  version: string
  config: Ontology
}

export type SupportedFeaturesNode = {
  ip: string
  type: string
  features: string[]
}
